import React from "react";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { PartnerRevenueLogRecord } from "@/types/partner-dashboard";

type RevenueLogsPanelProps = {
  logs: PartnerRevenueLogRecord[];
  loading?: boolean;
  title?: string;
};

function formatLogAmount(amount: number, currency: string): string {
  if (currency === "EUR") {
    return `€${amount.toLocaleString("en-GB")}`;
  }
  return `${currency}${amount.toLocaleString("en-GB")}`;
}

export const RevenueLogsPanel = ({
  logs,
  loading = false,
  title,
}: RevenueLogsPanelProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = React.useState(true);
  const [currentPage, setCurrentPage] = React.useState(1);
  const PAGE_SIZE = 3;
  const resolvedTitle = title ?? t("billing.revenueLogs.title");

  const totalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
  const paginatedLogs = React.useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return logs.slice(start, start + PAGE_SIZE);
  }, [logs, currentPage]);

  React.useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const pageNumbers = React.useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const result: Array<number | "..."> = [1];
    if (currentPage > 3) result.push("...");
    const middleStart = Math.max(2, currentPage - 1);
    const middleEnd = Math.min(totalPages - 1, currentPage + 1);
    for (let page = middleStart; page <= middleEnd; page += 1) {
      result.push(page);
    }
    if (currentPage < totalPages - 2) result.push("...");
    result.push(totalPages);

    return result;
  }, [currentPage, totalPages]);

  let logsContent: React.JSX.Element;

  if (loading) {
    logsContent = (
      <div className="px-6 py-3 text-xs text-zinc-500">
        {t("billing.revenueLogs.loading")}
      </div>
    );
  } else if (logs.length === 0) {
    logsContent = (
      <div className="px-6 py-3 text-xs text-zinc-500">
        {t("billing.revenueLogs.empty")}
      </div>
    );
  } else {
    logsContent = (
      <div>
        {paginatedLogs.map((log, index) => (
          <div
            key={log.id}
            className={cn(
              "grid grid-cols-[1fr_auto] items-center border-b border-[#E5E5E5] px-6 py-2 text-xs",
              index % 2 === 1 ? "bg-zinc-100/40" : "bg-white",
            )}
          >
            <span className="text-zinc-900">
              {format(new Date(log.createdAt), "dd.MM.yyyy.")}
            </span>
            <span className="text-zinc-900">
              {formatLogAmount(log.amount, log.currency)}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-center gap-2 px-6 py-3 text-xs text-zinc-900">
          <Button
            type="button"
            variant="span"
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            disabled={currentPage <= 1}
            className={cn(
              "px-1",
              currentPage <= 1 && "cursor-not-allowed text-zinc-400",
            )}
          >
            {t("billing.revenueLogs.previous")}
          </Button>
          {pageNumbers.map((page, index) =>
            page === "..." ? (
              <span key={`ellipsis-${index}`} className="px-1 text-zinc-500">
                ...
              </span>
            ) : (
              <Button
                key={page}
                type="button"
                variant="span"
                onClick={() => setCurrentPage(page)}
                className={cn(
                  "min-w-6 rounded px-1.5 py-0.5",
                  page === currentPage
                    ? "bg-zinc-100 font-medium"
                    : "text-zinc-700",
                )}
              >
                {page}
              </Button>
            ),
          )}
          <Button
            type="button"
            variant="span"
            onClick={() =>
              setCurrentPage((page) => Math.min(totalPages, page + 1))
            }
            disabled={currentPage >= totalPages}
            className={cn(
              "px-1",
              currentPage >= totalPages && "cursor-not-allowed text-zinc-400",
            )}
          >
            {t("billing.revenueLogs.next")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[#E5E5E5] bg-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
      <Button
        type="button"
        variant="span"
        aria-expanded={isOpen}
        aria-label={resolvedTitle}
        className="flex h-10 w-full items-center justify-between rounded-md px-6 py-2 text-left"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="inline-flex h-[20px] w-[86px] items-center text-sm font-normal leading-normal text-[#826BFF]">
          {resolvedTitle}
        </span>
        <ChevronDown
          className={cn(
            "size-4 text-zinc-900 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </Button>

      {isOpen ? (
        <div className="border-t border-[#E5E5E5]">
          <div className="grid grid-cols-[1fr_auto] items-center border-b border-[#E5E5E5] px-6 py-2 text-xs text-zinc-500">
            <span>{t("billing.revenueLogs.date")}</span>
            <span>{t("billing.revenueLogs.logs")}</span>
          </div>
          {logsContent}
        </div>
      ) : null}
    </div>
  );
};
